import CustomTestSettings from "@/Components/CustomTestSettings"
import Stepper from "@/Components/Stepper"
import SelectMapModal from "@/Components/SelectMapModal"
import { useAppSelector } from "@/store/hooks"
import { debounce, getUserCreds, socket } from "@/utils/utils"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { mapDesigns } from "@mono/mapDesigns"
import classNames from "classnames"
import NewGamePlayerList from "./NewGamePlayerList"
import { featureFlags } from "@mono/constants"
import { testSettings } from "@/utils/testSettings"
import TestSettingsModal from "@/Components/TestSettingsModal"
import { getActivePreset } from "@/utils/testSettingsStorage"
import { getGamePacePreference, setGamePacePreference } from "@/utils/gamePacePreference"
import FastPaceWarningModal from "@/Modals/FastPaceWarningModal"
import { NEW_PLAYER_GAMES_THRESHOLD } from "@mono/constants"

interface NewGameSettingsProps {
  myGameId: string
}

// Each map gets the turn count it is actually paced for: the mini map loops back
// on itself much faster, so 50 turns there drags long past the point the game is
// decided.
const MINI_MAP_ID = "1"
const MINI_MAP_MAX_TURNS = 35
const DEFAULT_MAX_TURNS = 50

const getMaxTurnsForMap = (mapId?: string) => (mapId === MINI_MAP_ID ? MINI_MAP_MAX_TURNS : DEFAULT_MAX_TURNS)

interface INewGameButtonItem {
  text: string
  icon: JSX.Element
  onClick: () => void
  className: string
  visible: boolean
}

// Non-hosts see the settings the host picked, but as plain values — disabled
// inputs read as "editable, but broken".
const ReadOnlyValue: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-gray-300 font-third text-lg sm:text-2xl">{children}</div>
)

const NewGameSettings: React.FC<NewGameSettingsProps> = ({ myGameId }) => {
  const myGame = useAppSelector((state) => state.global.myGame)
  const user = getUserCreds()
  const isHost = myGame?.host === user.email

  const [activeTestSettings, setActiveTestSettings] = useState<ITestNewGameSettings>(
    () => getActivePreset()?.settings || testSettings
  )
  const [customTestSettings, setCustomTestSettings] = useState(JSON.stringify(getActivePreset()?.settings || testSettings))
  const [isTestSettingsModalOpen, setIsTestSettingsModalOpen] = useState(false)
  const [enableTestSettings, setEnableTestSettings] = useState(featureFlags.showTestSettings)
  const [formData, setFormData] = useState<Partial<IGameSettings>>({
    ...(myGame?.settings || {}),
  })
  const [removingBots, setRemovingBots] = useState<Set<string>>(new Set())
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false)
  const [isFastPaceWarningOpen, setIsFastPaceWarningOpen] = useState(false)
  const nav = useNavigate()

  // Played-games count off the account. It lives flat on the user document and
  // nested on the seeded ones, hence both reads; an account that never loaded
  // simply is not treated as new, since warning a veteran every time they reach
  // for this toggle is a worse failure than not warning a newcomer once.
  const gamesPlayed = useAppSelector(
    (state) => state.global.userData?.gamesPlayed ?? state.global.userData?.stats?.gamesPlayed
  )
  const isNewPlayer = gamesPlayed !== undefined && gamesPlayed < NEW_PLAYER_GAMES_THRESHOLD
  // Nobody here but bots. Same question the in-game control asks (see
  // FastPaceControl): with no other human in the room the host's click is the
  // whole decision, so a player who has barely played gets told what they are
  // choosing before it is made for the rest of the table.
  const isBotsOnlyLobby = !(myGame?.players || []).some(
    (player) => player?.email !== user.email && !player?.bot && !player?.email?.includes?.("bot")
  )

  useEffect(() => {
    if (!isHost && myGame?.settings) {
      setFormData(myGame.settings)
    }
    if (isHost && myGame.settings && Object.keys(formData).length === 0) {
      setFormData(myGame.settings)
    }
  }, [myGame?.settings])

  useEffect(() => {
    setFormData(myGame?.settings || {})
  }, [])

  // Start the lobby on the pace this player last chose. Only the host can change
  // settings, so only the host pushes their remembered pace to the game.
  const hasAppliedPacePreference = useRef(false)
  useEffect(() => {
    if (hasAppliedPacePreference.current || !isHost || !myGame?.settings) return
    hasAppliedPacePreference.current = true
    const preferred = getGamePacePreference()
    if (preferred === null || preferred === !!myGame.settings.isQuickGame) return
    setFormData((prev) => ({ ...prev, ...myGame.settings, isQuickGame: preferred }))
    emitUpdate({ isQuickGame: preferred })
  }, [isHost, myGame?.settings])

  // Opening the lobby snaps max turns to whatever the current map is paced for,
  // so a game never starts on a turn count left over from a different map.
  const hasAppliedMapMaxTurns = useRef(false)
  useEffect(() => {
    if (hasAppliedMapMaxTurns.current || !isHost || !myGame?.settings) return
    hasAppliedMapMaxTurns.current = true
    const maxTurns = getMaxTurnsForMap(myGame.settings.mapId)
    if (maxTurns === myGame.settings.maxTurns) return
    setFormData((prev) => ({ ...myGame.settings, ...prev, maxTurns }))
    emitUpdate({ maxTurns })
  }, [isHost, myGame?.settings])

  const setFields = (data: Partial<typeof formData>) => {
    setFormData({
      ...formData,
      ...data,
    })
    debouncedEmitUpdate(data)
  }
  const emitUpdate = (settings: Partial<IGameSettings>) => {
    socket.emit("lobby.hostChangeGameSettings", {
      ...settings,
      id: myGame?.id || "",
    })
  }
  const debouncedEmitUpdate = useCallback(debounce(emitUpdate, 300), [myGame?.id])

  const applyGamePace = (isQuickGame: boolean) => {
    setGamePacePreference(isQuickGame)
    setFields({ isQuickGame })
  }

  // Turning it on is the only direction worth a warning: going back to normal
  // pace takes nothing away from anyone.
  const handleGamePaceChange = (isQuickGame: boolean) => {
    if (isQuickGame && isNewPlayer && isBotsOnlyLobby) {
      setIsFastPaceWarningOpen(true)
      return
    }
    applyGamePace(isQuickGame)
  }

  const onClickOnStartGame = () => {
    socket.emit(
      "newgame.startGame",
      myGameId,
      enableTestSettings ? customTestSettings : "",
      activeTestSettings.challengeId
    )
  }

  const handleLeave = () => {
    if (!myGame) return

    socket.emit("game.disconnect-player", myGameId, 0)

    nav("/lobby")
  }

  const handleAddBotPlayer = () => {
    socket.emit("newgame.add-bot", myGameId)
  }

  const handleRemoveBot = (botId: string) => {
    // Add bot to removing set for loading state
    setRemovingBots((prev) => new Set(prev).add(botId))

    socket.emit("newgame.remove-bot", myGameId, botId, (success: boolean) => {
      // Remove bot from removing set
      setRemovingBots((prev) => {
        const newSet = new Set(prev)
        newSet.delete(botId)
        return newSet
      })

      if (!success) {
        console.error("Failed to remove bot")
      }
    })
  }

  if (!myGame) return null
  const canStart = myGame.players.length >= myGame.settings.minPlayers

  const buttons: INewGameButtonItem[] = [
    {
      text: "Add Bot",
      icon: <i className="fas fa-robot fa-lg"></i>,
      onClick: handleAddBotPlayer,
      className:
        " from-blue-700 via-indigo-700 to-blue-800 hover:from-blue-800 hover:via-indigo-800 hover:to-blue-900    border border-blue-600/30 ",
      visible: isHost,
    },
    {
      text: "Leave",
      icon: <i className="fas fa-sign-out-alt fa-lg"></i>,
      onClick: handleLeave,
      className:
        " from-red-700 via-rose-700 to-red-800 hover:from-red-800 hover:via-rose-800 hover:to-red-900    border border-red-600/30 ",
      visible: true,
    },
  ]
  return (
    <div className="relative h-full pt-36 md:pt-8 overflow-auto  bg-[#0c0c0b] border-[#23201b] border p-8 rounded-xl grid grid-cols-[min-content_auto] gap-10 w-full items-center gap-x-16 pb-[max(6rem,var(--safe-bottom))] pl-[max(2rem,var(--safe-left))] pr-[max(4rem,var(--safe-right))] ">
      {/* Top Right Buttons */}
      <div className="absolute top-32 md:top-0 right-0 flex gap-4 sm:gap-8 mt-8 mr-[max(2rem,var(--safe-right))] z-20 sm:grid sm:grid-cols-1">
        {buttons
          .filter((button) => button.visible)
          .map((button) => (
            <button
              key={button.text}
              onClick={button.onClick}
              className={classNames(
                button.className,
                "flex new-game-btn items-center bg-gradient-to-r rounded-xl shadow-lg transition-all duration-300 hover:scale-105 active:scale-100  backdrop-blur-sm "
              )}
            >
              {button.icon}
              {button.text}
            </button>
          ))}
      </div>

      {!isHost && (
        <div className="col-span-2 flex items-center gap-3 text-gray-400 text-sm sm:text-lg font-third -mt-2">
          <i className="fas fa-lock"></i>
          <span>Only the host can change these settings.</span>
        </div>
      )}

      <div className="text-white font-fourth  lobby-input-label whitespace-nowrap">Game Name</div>
      {isHost ? (
        <input
          className="input2 w-1/3"
          onChange={(e) =>
            setFields({
              title: e.target.value,
            })
          }
          type="text"
          value={formData.title}
          data-testid="newgame.title"
          placeholder="Game Title"
        />
      ) : (
        <ReadOnlyValue>{formData.title}</ReadOnlyValue>
      )}
      <div className="text-white font-fourth  lobby-input-label whitespace-nowrap">Map</div>
      {isHost ? (
        <button
          onClick={() => setIsMapDialogOpen(true)}
          className="input2 w-1/3 text-left flex items-center justify-between"
        >
          <span>{mapDesigns.find((map) => map.id === formData.mapId)?.name || "Select Map"}</span>
          <i className="fas fa-chevron-down text-gray-400"></i>
        </button>
      ) : (
        <ReadOnlyValue>{mapDesigns.find((map) => map.id === formData.mapId)?.name || "—"}</ReadOnlyValue>
      )}
      {featureFlags.showTestSettings && isHost && (
        <div className="text-white font-fourth  lobby-input-label whitespace-nowrap">Enable Test Settings</div>
      )}
      {featureFlags.showTestSettings && isHost && (
        <div className="flex items-center gap-4">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={enableTestSettings}
              onChange={(e) => setEnableTestSettings(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
          <span className="text-gray-400 text-sm">{enableTestSettings ? "Enabled" : "Disabled"}</span>
        </div>
      )}
      {featureFlags.showTestSettings && isHost && (
        <div
          className={classNames("col-span-2 flex items-center gap-6 flex-wrap", {
            "opacity-50 pointer-events-none": !enableTestSettings,
          })}
        >
          <button
            onClick={() => setIsTestSettingsModalOpen(true)}
            className="ts-btn bg-indigo-700 hover:bg-indigo-600 flex items-center gap-2"
          >
            <i className="fas fa-sliders-h"></i>
            Configure Test Settings
          </button>
          <span className="text-gray-500 text-sm">Active: {getActivePreset()?.name || "Default"}</span>
          <CustomTestSettings value={customTestSettings} setValue={setCustomTestSettings}></CustomTestSettings>
        </div>
      )}
      <div className="text-white font-fourth  lobby-input-label whitespace-nowrap">Max turns</div>
      {isHost ? (
        <div className="flex gap-8 items-center h-fit" data-testid="newgame.max-turns">
          <Stepper
            value={Number(formData.maxTurns)}
            onChange={(value) =>
              setFields({
                maxTurns: value,
              })
            }
            max={Infinity}
            min={0}
          ></Stepper>
        </div>
      ) : (
        <ReadOnlyValue>{formData.maxTurns}</ReadOnlyValue>
      )}
      <div className="text-white font-fourth  lobby-input-label whitespace-nowrap">Starting funds</div>
      {isHost ? (
        <Stepper
          value={Number(formData.startingFunds)}
          onChange={(value) =>
            setFields({
              startingFunds: value,
            })
          }
          max={2000}
          min={0}
          step={100}
        ></Stepper>
      ) : (
        <ReadOnlyValue>{formData.startingFunds}</ReadOnlyValue>
      )}
      <div className="text-white font-fourth  lobby-input-label whitespace-nowrap">Game Pace</div>
      {isHost ? (
        <div className="flex items-center gap-4">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!!formData.isQuickGame}
              onChange={(e) => handleGamePaceChange(e.target.checked)}
              className="sr-only peer"
              data-testid="newgame.quick-game"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
          <span className="text-gray-400 text-sm">
            {formData.isQuickGame
              ? "Quick — shorter timers, faster animations"
              : "Normal"}
          </span>
        </div>
      ) : (
        <ReadOnlyValue>
          {formData.isQuickGame ? "Quick — shorter timers, faster animations" : "Normal"}
        </ReadOnlyValue>
      )}

      <NewGamePlayerList
        players={myGame?.players || []}
        hostEmail={myGame?.host}
        isHost={isHost}
        onRemoveBot={handleRemoveBot}
        removingBots={removingBots}
      />

      {/* Start Game closes the panel as its own footer: the negative margins
          match the panel's own padding (p-8 plus pr-16 / pb-24) so the divider
          runs edge to edge and the button centres on the card, not on the
          settings column. */}
      {isHost && (
        <div className="col-span-2 -ml-8 -mr-16 -mb-24 flex flex-col items-center gap-2 border-t border-[#23201b] px-8 pt-6 pb-8">
          <button
            onClick={onClickOnStartGame}
            disabled={!canStart}
            data-testid="newgame.start-btn"
            className={classNames(
              "flex items-center justify-center gap-3 sm:gap-4 w-full max-w-md px-10 py-3 sm:px-12 sm:py-5 rounded-xl sm:rounded-2xl text-white text-2xl sm:text-4xl font-semibold tracking-wide bg-gradient-to-r from-emerald-700 via-green-700 to-emerald-800 border border-emerald-600/30 shadow-lg shadow-emerald-950/50 transition-all duration-300 active:scale-100",
              canStart
                ? "hover:from-emerald-800 hover:via-green-700 hover:to-emerald-900 hover:scale-105"
                : "opacity-40 cursor-not-allowed"
            )}
          >
            <i className="fas fa-play"></i>
            Start Game
          </button>
          {!canStart && (
            <span className="text-gray-400 text-sm sm:text-lg font-third">
              Need at least {myGame.settings.minPlayers} players to start
            </span>
          )}
        </div>
      )}

      {/* What fast pace actually costs a new player, before they hand it to the
          whole table. See FastPaceWarningModal — the in-game button raises the
          same panel. */}
      {isFastPaceWarningOpen && (
        <FastPaceWarningModal
          backdrop
          onCancel={() => setIsFastPaceWarningOpen(false)}
          onConfirm={() => {
            setIsFastPaceWarningOpen(false)
            applyGamePace(true)
          }}
        />
      )}

      {/* Map Selection Dialog */}
      <SelectMapModal
        isOpen={isMapDialogOpen}
        onClose={() => setIsMapDialogOpen(false)}
        selectedMapId={formData.mapId}
        onSelectMap={(mapId) =>
          setFields({
            mapId,
            maxTurns: getMaxTurnsForMap(mapId),
          })
        }
      />

      {/* Test Settings Dialog */}
      {featureFlags.showTestSettings && (
        <TestSettingsModal
          isOpen={isTestSettingsModalOpen}
          onClose={() => setIsTestSettingsModalOpen(false)}
          onActiveSettingsChange={(settings) => {
            setActiveTestSettings(settings)
            setCustomTestSettings(JSON.stringify(settings))
            // Keep the actual game map in sync with the test preset's map.
            if (settings.mapId && settings.mapId !== formData.mapId) {
              setFields({ mapId: settings.mapId })
            }
          }}
        />
      )}
    </div>
  )
}

export default NewGameSettings
